import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useHubNotificationsStore } from '../contexts/HubNotificationsContext';

/**
 * Drawer open state for the current page, reading the shared hub-bell
 * snapshot so the count does not rebuild on every route change.
 */
export default function useHubNotifications({ onInvitationAccepted } = {}) {
  const navigate = useNavigate();
  const store = useHubNotificationsStore();
  const [open, setOpen] = useState(false);
  const invitationsRef = useRef(store.invitations);
  const markReadFiredForOpenRef = useRef(false);
  const onAcceptedRef = useRef(onInvitationAccepted);
  onAcceptedRef.current = onInvitationAccepted;
  const {
    invitations,
    invitationsLoading,
    accountActions,
    accountActionsLoading,
    actionLoadingIds,
    badge,
    loading,
    fetchAccountActions,
    acceptInvitation,
    declineInvitation,
    markInvitationsReadLocally,
  } = store;
  invitationsRef.current = invitations;

  useEffect(() => {
    if (!open) return undefined;
    fetchAccountActions();
    return undefined;
  }, [open, fetchAccountActions]);

  useEffect(() => {
    if (!open) {
      markReadFiredForOpenRef.current = false;
      return undefined;
    }
    if (markReadFiredForOpenRef.current) return undefined;
    markReadFiredForOpenRef.current = true;

    const unreadIds = (Array.isArray(invitationsRef.current) ? invitationsRef.current : [])
      .filter((invitation) => {
        const status = String(invitation?.status || 'pending').toLowerCase();
        return status === 'pending' && !invitation?.readAt && invitation?._id;
      })
      .map((invitation) => invitation._id);
    if (unreadIds.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('/api/invitations/mark-read', { ids: unreadIds });
        if (cancelled) return;
        if ((res?.data?.updatedCount || 0) > 0) {
          markInvitationsReadLocally(unreadIds);
        }
      } catch {
        /* badge may remain until the next fetch */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, markInvitationsReadLocally]);

  const onAcceptInvitation = useCallback(
    async (invitationId) => {
      await acceptInvitation(invitationId);
      await onAcceptedRef.current?.();
    },
    [acceptInvitation]
  );

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((value) => !value), []);

  const drawerProps = useMemo(
    () => ({
      invitations,
      invitationsLoading,
      accountActions,
      accountActionsLoading,
      actionLoadingIds,
      onAcceptInvitation,
      onDeclineInvitation: declineInvitation,
      onActionCta: (cta) => {
        close();
        if (cta?.type === 'route' && cta?.to) {
          requestAnimationFrame(() => navigate(cta.to));
        } else if (cta?.type === 'external' && cta?.href) {
          requestAnimationFrame(() => window.open(cta.href, '_blank', 'noopener,noreferrer'));
        }
      },
      onViewInvitations: () => {
        close();
        navigate('/invitations');
      },
    }),
    [
      invitations,
      invitationsLoading,
      accountActions,
      accountActionsLoading,
      actionLoadingIds,
      declineInvitation,
      onAcceptInvitation,
      close,
      navigate,
    ]
  );

  return {
    open,
    close,
    toggle,
    badge,
    loading,
    drawerProps,
  };
}
